import { StateCreator } from 'zustand';
import { ActiveRefill } from '@qmd-types/orders';
import {
  PatientCreditCard,
  PatientProfile,
  PatientSubscription,
} from '@qmd-types/patient';
import { UpcomingVisit, VisitDetail } from '@qmd-types/visits';
import { AvailableSubscription } from '@types';
import { sortPatientSubscriptionsByActiveAndMostRecent } from '@pages/SchedulePage/utils';

const PATIENT_COMPLETE_IDENTITY_VERIFICATION =
  'PATIENT_COMPLETE_IDENTITY_VERIFICATION';
const PATIENT_RESET_PATIENT = 'PATIENT_RESET_PATIENT';
const PATIENT_SET_ACTIVE_REFILL = 'PATIENT_SET_ACTIVE_REFILL';
const PATIENT_SET_CREDIT_CARDS = 'PATIENT_SET_CREDIT_CARDS';
const PATIENT_SET_PATIENT_PROFILE = 'PATIENT_SET_PATIENT_PROFILE';
const PATIENT_SET_UPCOMING_VISITS = 'PATIENT_SET_UPCOMING_VISITS';
const PATIENT_SET_USER_SUBSCRIPTIONS = 'PATIENT_SET_USER_SUBSCRIPTIONS';
const PATIENT_SET_VISIT_DETAIL = 'PATIENT_SET_VISIT_DETAIL';
const PATIENT_SET_SUBSCRIPTIONS_AVAILABLE_TO_PATIENT =
  'PATIENT_SET_SUBSCRIPTIONS_AVAILABLE_TO_PATIENT';

interface PatientState {
  activeRefill?: ActiveRefill;
  creditCards?: PatientCreditCard[];
  patientProfile?: PatientProfile;
  upcomingVisits?: UpcomingVisit[];
  visitDetail?: VisitDetail;
  patientSubscriptions?: PatientSubscription[];
  subscriptionsAvailableToPatient?: AvailableSubscription[];
}

interface PatientActions {
  clearPatientProfile: () => void;
  completeIdentityVerification: () => void;
  decreaseIdVerificationRetryCount: () => void;
  resetPatient: () => void;
  setActiveRefill: (activeRefill: ActiveRefill) => void;
  setPatientCreditCards: (creditCards: PatientCreditCard[]) => void;
  setPatientProfile: (patientProfile: PatientProfile) => void;
  setUpcomingVisits: (upcomingVisits: UpcomingVisit[]) => void;
  setVisitDetail: (visitDetail: VisitDetail) => void;
  setPatientSubscriptions: (
    patientSubscriptions: PatientSubscription[],
  ) => void;
  setSubscriptionsAvailableToPatient: (
    subscriptionsAvailableToPatient: AvailableSubscription[],
  ) => void;
}

export type PatientSlice = {
  patient: PatientState & PatientActions;
};

const initialState: PatientState = {
  activeRefill: undefined,
  creditCards: undefined,
  patientProfile: undefined,
  upcomingVisits: undefined,
  visitDetail: undefined,
  patientSubscriptions: undefined,
  subscriptionsAvailableToPatient: undefined,
};

export const createPatientSlice: StateCreator<
  PatientSlice,
  [['zustand/devtools', never]]
> = (set) => ({
  patient: {
    ...initialState,
    clearPatientProfile: () => {
      set(
        (state) => {
          return {
            patient: {
              ...state.patient,
              patientProfile: undefined,
            },
          };
        },
        false,
        PATIENT_SET_PATIENT_PROFILE,
      );
    },
    completeIdentityVerification: () => {
      set(
        ({ patient }) => {
          const existingProfile =
            patient.patientProfile ?? ({} as PatientProfile);

          return {
            patient: {
              ...patient,
              patientProfile: {
                ...existingProfile,
                identityVerification: {
                  ...existingProfile.identityVerification,
                  isCompleted: true,
                  isLegacyCompleted: false,
                  isCompletedWithErrors: false,
                },
              },
            },
          };
        },
        false,
        PATIENT_COMPLETE_IDENTITY_VERIFICATION,
      );
    },
    decreaseIdVerificationRetryCount: () => {
      set(
        ({ patient }) => {
          const existingProfile =
            patient.patientProfile ?? ({} as PatientProfile);
          return {
            patient: {
              ...patient,
              patientProfile: {
                ...existingProfile,
                identityVerification: {
                  ...existingProfile.identityVerification,
                  remainingAttemptsCount:
                    existingProfile.identityVerification
                      .remainingAttemptsCount - 1,
                },
              },
            },
          };
        },
        false,
        PATIENT_COMPLETE_IDENTITY_VERIFICATION,
      );
    },
    resetPatient: () => {
      set(
        (state) => {
          return {
            patient: {
              ...state.patient,
              activeRefill: undefined,
              creditCards: undefined,
              patientProfile: undefined,
              upcomingVisits: undefined,
              visitDetail: undefined,
            },
          };
        },
        false,
        PATIENT_RESET_PATIENT,
      );
    },
    setActiveRefill: (activeRefill) => {
      set(
        (state) => {
          return {
            patient: {
              ...state.patient,
              activeRefill,
            },
          };
        },
        false,
        PATIENT_SET_ACTIVE_REFILL,
      );
    },
    setPatientCreditCards: (creditCards) => {
      set(
        (state) => ({ patient: { ...state.patient, creditCards } }),
        false,
        PATIENT_SET_CREDIT_CARDS,
      );
    },
    setPatientProfile: (patientProfile) => {
      set(
        (state) => {
          return {
            patient: {
              ...state.patient,
              patientProfile,
            },
          };
        },
        false,
        PATIENT_SET_PATIENT_PROFILE,
      );
    },
    setUpcomingVisits: (upcomingVisits) => {
      set(
        (state) => {
          return {
            patient: {
              ...state.patient,
              upcomingVisits,
            },
          };
        },
        false,
        PATIENT_SET_UPCOMING_VISITS,
      );
    },
    setVisitDetail: (visitDetail) => {
      set(
        (state) => {
          return {
            patient: {
              ...state.patient,
              visitDetail,
            },
          };
        },
        false,
        PATIENT_SET_VISIT_DETAIL,
      );
    },
    setPatientSubscriptions: (patientSubscriptions) => {
      set(
        (state) => {
          return {
            patient: {
              ...state.patient,
              patientSubscriptions:
                sortPatientSubscriptionsByActiveAndMostRecent(
                  patientSubscriptions,
                ),
            },
          };
        },
        false,
        PATIENT_SET_USER_SUBSCRIPTIONS,
      );
    },
    setSubscriptionsAvailableToPatient: (subscriptionsAvailableToPatient) => {
      set(
        (state) => {
          return {
            patient: { ...state.patient, subscriptionsAvailableToPatient },
          };
        },
        false,
        PATIENT_SET_SUBSCRIPTIONS_AVAILABLE_TO_PATIENT,
      );
    },
  },
});
